// @flow

import { createGlobalStyle } from 'styled-components';
import { rem, invert, transparentize } from 'polished';
import { generateMedia } from 'styled-media-query';
import { normalize } from 'styled-normalize';

import dkddgFontEot from '../assets/fonts/dk_drop_dead_gorgeous/3AA7D8_0_0.eot';
import dkddgFontWoff from '../assets/fonts/dk_drop_dead_gorgeous/3AA7D8_0_0.woff';
import dkddgFontWoff2 from '../assets/fonts/dk_drop_dead_gorgeous/3AA7D8_0_0.woff2';
import dkddgFontTtf from '../assets/fonts/dk_drop_dead_gorgeous/3AA7D8_0_0.ttf';

type FontFaceConfiguration = {
  fontFamily: string,
  fontFilePath?: string,
  fontStretch?: string,
  fontStyle?: string,
  fontVariant?: string,
  fontWeight?: string,
  fileFormats?: string[],
  formatHint?: boolean,
  localFonts?: string[],
  unicodeRange?: string,
  fontDisplay?: string,
  fontVariationSettings?: string,
  fontFeatureSettings?: string,
  external?: boolean,
};

export const mediaBreakpointsRaw = {
  mobile: 320,
  tablet: 768,
  desktop: 1024,
  wide: 1280,
  ultrawide: 2000,
};

export const mediaBreakpoints = {
  mobile: `${mediaBreakpointsRaw.mobile}px`,
  desktop: `${mediaBreakpointsRaw.desktop}px`,
  wide: `${mediaBreakpointsRaw.wide}px`,
  ultrawide: `${mediaBreakpointsRaw.ultrawide}px`,
};

export const media = generateMedia(mediaBreakpoints);

export const fontSizes = {
  normal: {
    size: rem('15px'),
    lineHeight: rem('21px'),
  },
  small: {
    size: rem('10px'),
    lineHeight: rem('14px'),
  },
};

export const gridTheme = {
  columns: {
    xs: 12,
    md: 12,
  },
  gutterWidth: {
    xs: 0,
    md: 2.68,
  },
  container: {
    xs: 'full', // 'full' = max-width: 100%
    md: 90, // max-width: 1280px | 90rem = 1440px
  },
  breakpoints: {
    xs: 1,
    md: 64, // 1024px
  },
  paddingWidth: {
    xs: 1.7,
    md: 0,
  },
};

export const colors = {
  black: '#000',
  white: '#fff',
  red: '#ff0003',
  yellow: '#ffc514',
  grey: '#a1a4b8',
  darkGrey: '#232323',
  darkWhite: '#fefefe',
  darkYellow: '#664e15',
  lightYellow: '#ffd145',
  darkRed: '#cc2124',
};

const fonts: { [string]: FontFaceConfiguration } = {
  politicaBlack: {
    fontFamily: 'politica,sans-serif',
    fontDisplay: 'swap',
    fontWeight: '900',
    fontStyle: 'normal',
    external: true,
  },
  politicaExtraBold: {
    fontFamily: 'politica,sans-serif',
    fontDisplay: 'swap',
    fontWeight: '700',
    fontStyle: 'normal',
    external: true,
  },
  dropDeadGorgeousRegular: {
    fontFamily: 'DK Drop Dead Gorgeous Regular',
    fileFormats: ['otf'],
    fontFilePath: '/fonts/dk_drop_dead_gorgeous',
    fontDisplay: 'swap',
  },
};

export const fontFamily = {
  primary: {
    black: fonts.politicaBlack,
    bold: fonts.politicaExtraBold,
  },
  secondary: {
    regular: fonts.dropDeadGorgeousRegular,
  },
};

export const colorPalette = {
  dark: {
    active: colors.black,
    hover: colors.black,
    inverted: invert(colors.black),
    foreGround: colors.white,
  },
  primary: {
    active: colors.yellow,
    borderActive: colors.white,
    activeDarker: colors.darkYellow,
    hover: colors.lightYellow,
    inverted: invert(colors.yellow),
    foreGround: colors.black,
  },
  secondary: {
    active: colors.darkGrey,
    hover: colors.darkGrey,
    inverted: invert(colors.darkGrey),
    foreGround: colors.white,
  },
  danger: {
    active: colors.red,
    hover: colors.red,
    inverted: invert(colors.red),
    foreGround: colors.white,
  },
};

export const sizes = {
  borderRadius: '2px',
  padding: '20px',
  paddingBig: '70px',
  paddingRightSide: '25px',
  letterSpacing: {
    normal: '0.8px',
    large: '2px',
  },
};

export const timings = {
  shadow: '200ms',
  hover: '400ms',
};

export default createGlobalStyle`
  ${normalize}

  @font-face {
    font-family: 'DK Drop Dead Gorgeous Regular';
    src: url('${dkddgFontEot}');
    src: url('${dkddgFontEot}?#iefix') format('embedded-opentype'),
      url('${dkddgFontWoff2}') format('woff2'),
      url('${dkddgFontWoff}') format('woff'),
      url('${dkddgFontTtf}') format('truetype');
  }

  * {
    box-sizing: border-box;
  }

  body,
  button,
  input,
  select,
  textarea {
    font-family: ${fontFamily.primary.bold.fontFamily};
    font-weight: ${fontFamily.primary.bold.fontWeight};
  }

  html {
    background-color: ${colorPalette.dark.active};
    font-size: ${fontSizes.normal.size};
    letter-spacing: ${sizes.letterSpacing.normal};
    line-height: ${fontSizes.normal.lineHeight};
  }

  body {
    color: ${colors.white};
    overflow-x: hidden;
    &.noscroll {
      overflow: hidden;
      padding-right: 15px;
    }
  }

  #root {
    /* max-width: ${mediaBreakpoints.wide}; */
    margin: 0 auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  p {
    margin: 10px 0;
  }

  a {
    color: inherit;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  h1, h2 {
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: ${sizes.letterSpacing.large};
    margin-top: 0;
  }

  h1 {
    font-size: ${rem('28px')};
    line-height: ${rem('36px')};
  }

  h1.big {
    font-size: ${rem('40px')};
    line-height: ${rem('48px')};
  }

  /*
  h2 {
    font-size: ${rem('10px')};
    line-height: ${rem('16px')};
  }
  */

  h2 {
    font-size: ${rem('16px')};
    letter-spacing: 0.6px;
    margin-bottom: 5px;
    user-select: none;

    &.mobile-big {
      font-size: ${rem('28px')};
      line-height: ${rem('34px')};
    }
  }

  h3 {
    margin: 0;
    font-size: ${rem('15px')};
  }

  b {
    color: ${colors.yellow};
  }

  .headline {
    text-transform: uppercase;
    font-size: ${rem(28)};
    line-height: 36px;
    font-family: ${fontFamily.primary.black.fontFamily};
    font-weight: ${fontFamily.primary.black.fontWeight};
    span {
      color: ${colors.yellow};
    }
  }

  .ReactModal__Overlay {
    background-color: ${transparentize(0.1, colorPalette.secondary.active)} !important;
    z-index: 10;
    opacity: 0;
    transition: opacity 200ms ease-in-out;
  }

  .ReactModal__Overlay--after-open{
      opacity: 1;
  }

  .ReactModal__Overlay--before-close{
      opacity: 0;
  }

  .grecaptcha-badge {
    display: none;
  }

  .error {
    padding-top: 5px;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    animation: transitionError 600ms ease-in-out 0s forwards;
    color: ${colors.red};
  }

  label {
    user-select: none;
  }

  @keyframes transitionError {
    0%   {
      margin-left: 30px;
      opacity:0;
    }
    100% {
      margin-left: 0;
      opacity:1;
      max-height: none;
    }
  }  

  .right-side {
    padding: 0 ${sizes.padding} 30px ${sizes.padding};
    overflow: hidden;
    margin-top: 75px;
    min-height: 100vh;

    h2 {
      display: none;
    }
    
    ${media.greaterThan('desktop')`
      padding: 105px ${sizes.padding} 130px ${sizes.padding};
      margin-top: 0;
      min-height: auto;
      h2 {
        display: block;
        margin-top: 5px;
      }
    `}
  }  

  .row {
    margin-left: 0;
    margin-right: 0;
  }

  .groupButtons {
    button {
      margin-bottom: 10px;
    }
  }

  .debug {
    text-align: center;
    margin-top: 10px;
    font-size: ${rem('12px')};
    opacity: 0.3;
    button {
      background-color: #fff;
    }
  }

  .modalError {
    .modal-header {
      background-color: transparent !important;
      z-index: 4 !important;
    }
  }

  ${media.greaterThan('desktop')`
    h1 {
      font-size: ${rem('60px')};
      line-height: ${rem('68px')};
    }

    h1.big {
      font-size: ${rem('70px')};
      line-height: ${rem('76px')};
    }

    h2 {
      font-size: ${rem('28px')};
      line-height: ${rem('34px')};
    }

    .ReactModal__Body--open {
      overflow: hidden;
    }

  `}


  /********** GOOGLE PLACES **********/
  .pac-container {
    margin-top: 30px;
    background-color: ${colors.black};
    border: none;

    &:after {
      display: none;
    }

    .pac-item {
      border: 2px solid ${colorPalette.secondary.hover};
      border-radius: ${sizes.borderRadius};
      padding: 15px 20px;
      margin-bottom: 5px;
      background: ${colorPalette.secondary.active};
      color: ${colorPalette.secondary.foreGround};
      text-align: left;
      text-transform: uppercase;
      font-family: ${fontFamily.primary.black.fontFamily};
      font-weight: ${fontFamily.primary.black.fontWeight};
      cursor: pointer;
      outline: 0;
      transition: 300ms all ease-out;
      user-select: none;

      .pac-icon {
        display: none;
      }

      span {
      font-family: ${fontFamily.primary.black.fontFamily};
      font-weight: ${fontFamily.primary.black.fontWeight};
        font-size: ${fontSizes.normal.size};
        color: ${transparentize(0.5, colorPalette.secondary.foreGround)};
      }

      .pac-item-query {
        .pac-matched {
          color: ${colorPalette.secondary.foreGround};
        }
      }

      ${media.greaterThan('desktop')`
        &:hover, &.pac-item-selected {
          background-color: transparent;
        }
      `}
    }
  }
`;
